<template>
    <v-dialog 
        v-model="value" 
        max-width="615px" 
        :persistent="persistent"
        @keydown.esc="handleEscape"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="dialogTitleId"
        :aria-describedby="dialogDescId">
        <v-card 
            class="pa-8 rounded-xl" 
            flat
            ref="dialogCard">
            <v-card-title :id="dialogTitleId">
                <v-row>
                    <v-col>
                        <v-row justify="center">
                            <v-icon 
                                class="ma-4 font-weigth-thin" 
                                size="76px" 
                                :color="iconColor"
                                :aria-label="getIconLabel()"
                                role="img">
                                {{ `mdi-${iconType}` }}
                            </v-icon>
                        </v-row>
                        <v-row 
                            class="text-large better-break" 
                            justify="center"
                            role="heading"
                            aria-level="1">
                            <span v-html="title"></span>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text class="my-0 py-0">
                <v-row class="ma-2">
                    <v-col>
                        <v-row 
                            class="mb-2 text-normal better-break" 
                            justify="center"
                            :id="dialogDescId">
                            <span v-html="subtitle"></span>
                        </v-row>
                        <v-row 
                            v-if="toggleItems" 
                            class="mb-2 text-normal better-break" 
                            justify="center"
                            role="group"
                            :aria-label="$t('accessibility.selectOption')">
                            <v-btn-toggle 
                                v-model="toggleItems.model"
                                color="primary"
                                @change="onToggle"
                                mandatory
                                :aria-label="$t('accessibility.toggleOptions')">
                                <v-btn 
                                    :value="toggleItems.value1"
                                    :aria-label="toggleItems.text1"
                                    :aria-pressed="toggleItems.model === toggleItems.value1 ? 'true' : 'false'">
                                    {{toggleItems.text1}}
                                </v-btn>
                                <v-btn 
                                    :value="toggleItems.value2"
                                    :aria-label="toggleItems.text2"
                                    :aria-pressed="toggleItems.model === toggleItems.value2 ? 'true' : 'false'">
                                    {{toggleItems.text2}}
                                </v-btn>
                            </v-btn-toggle>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="pb-4">
                <v-col cols="12">
                    <v-row justify="center">
                        <v-btn 
                            class="text-none text-normal white--text"
                            :color="type === 'delete' ? '#DD0D4D' : 'primary'"
                            :disabled="isFormProcessing"
                            :aria-busy="isFormProcessing ? 'true' : 'false'"
                            :aria-label="isFormProcessing ? $t('accessibility.loading') : btnOKText"
                            min-width="33%"
                            x-large 
                            depressed
                            @click="onOk"
                            ref="okButton">
                            {{ btnOKText }}
                            &nbsp;
                            <v-progress-circular 
                                v-if="isFormProcessing" 
                                indeterminate
                                size="20"
                                color="white"
                                aria-hidden="true">
                            </v-progress-circular>
                            <span v-if="isFormProcessing" class="sr-only">
                                {{$t('accessibility.loading')}}
                            </span>
                        </v-btn>
                        <v-btn 
                            class="text-none text-normal ml-4" 
                            min-width="33%" 
                            x-large 
                            depressed 
                            :class="{ 'white--text': type !== 'info' }" 
                            :color="type === 'info'? 'transparent': 'primary'" 
                            @click="onCancel"
                            :aria-label="btnCancelText"
                            ref="cancelButton">
                            {{ btnCancelText }}
                        </v-btn>
                    </v-row>
                </v-col>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<style scoped>
    .better-break {
        word-break: normal;
        word-wrap: normal;
        text-align: center;
    }
</style>

<script>
    import { FocusTrapManager } from '../utils/focusManagement';
    import { generateId } from '../utils/accessibility';

    export default {
        name: "confirmationDialog",
        props: {
            value: undefined,
            items: [],
            type: {
                type: String,
                default: 'info'
            },
            title: {
                type: String,
                default: 'Edit'
            },
            subtitle: {
                type: String,
                default: ''
            },
            btnOKText: {
                type: String,
                default: 'Confirm'
            },
            btnCancelText: {
                type: String,
                default: 'Cancel'
            },
            toggleItems: {
                type: Object,
                default: undefined
            },
            persistent: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                iconType: 'help-circle-outline',
                iconColor: 'primary',
                formProcess: false,
                focusTrap: null,
                dialogTitleId: generateId('dialog-title'),
                dialogDescId: generateId('dialog-desc')
            }
        },
        computed: {
            isFormProcessing() {
                return this.formProcess;
            },
        },
        mounted() {
            this.setTypeValues();
        },
        watch: {
            type() {
                this.setTypeValues();
            },
            value(newVal, oldVal) {
                this.formProcess = false;

                if (newVal && !oldVal) {
                    // Dialog is opening
                    this.$nextTick(() => {
                        this.activateFocusTrap();
                        this.announceDialog();
                    });
                } else if (!newVal && oldVal) {
                    // Dialog is closing
                    this.deactivateFocusTrap();
                }
            }
        },
        methods: {
            activateFocusTrap() {
                if (this.$refs.dialogCard) {
                    try {
                        this.focusTrap = new FocusTrapManager(this.$refs.dialogCard.$el, {
                            escapeDeactivates: !this.persistent,
                            clickOutsideDeactivates: !this.persistent,
                            returnFocusOnDeactivate: true,
                            initialFocus: this.$refs.okButton ? this.$refs.okButton.$el : undefined
                        });
                        this.focusTrap.activate();
                    } catch (error) {
                        console.warn('Focus trap activation failed:', error);
                    }
                }
            },

            deactivateFocusTrap() {
                if (this.focusTrap) {
                    this.focusTrap.deactivate();
                    this.focusTrap = null;
                }
            },

            announceDialog() {
                // Announce dialog opening to screen readers
                const dialogType = this.getDialogTypeLabel();
                this.$announcer.polite(`${dialogType}: ${this.title}`);
            },

            getDialogTypeLabel() {
                switch (this.type) {
                    case 'warning':
                        return this.$t('accessibility.warning');
                    case 'alert':
                        return this.$t('accessibility.alert');
                    case 'delete':
                        return this.$t('accessibility.deleteConfirmation');
                    case 'edit':
                        return this.$t('accessibility.editDialog');
                    default:
                        return this.$t('accessibility.dialog');
                }
            },

            getIconLabel() {
                switch (this.type) {
                    case 'warning':
                        return this.$t('accessibility.warningIcon');
                    case 'alert':
                        return this.$t('accessibility.alertIcon');
                    case 'delete':
                        return this.$t('accessibility.deleteIcon');
                    case 'edit':
                        return this.$t('accessibility.editIcon');
                    case 'resetPassword':
                        return this.$t('accessibility.resetPasswordIcon');
                    default:
                        return this.$t('accessibility.infoIcon');
                }
            },

            handleEscape() {
                if (!this.persistent) {
                    this.onCancel();
                }
            },

            setTypeValues() {
                switch (this.type) {
                    case 'warning':
                        this.iconType = 'alert-circle-outline';
                        this.iconColor = 'orange';
                        break;
                    case 'alert':
                        this.iconType = 'alert-circle-outline';
                        this.iconColor = 'red';
                        break;
                    case 'edit':
                        this.iconType = 'playlist-edit';
                        this.iconColor = 'primary';
                        break;
                    case 'delete':
                        this.iconType = 'close-circle-outline';
                        this.iconColor = '#DD0D4D';
                        break;
                    case 'resetPassword':
                        this.iconType = 'lock-outline';
                        this.iconColor = 'primary';
                        break;
                    case 'editLanguage':
                        this.iconType = 'help-circle-outline';
                        this.iconColor = 'primary';
                        break;
                    default:
                        this.iconType = 'help-circle-outline';
                        this.iconColor = 'primary';
                }
            },

            onOk() {
                this.formProcess = true;
                this.$emit('commit');
            },

            onCancel() {
                this.$emit('cancel');
            },

            onToggle() {
                this.$emit('toggle');
            }
        },

        beforeDestroy() {
            // Clean up focus trap
            this.deactivateFocusTrap();
        }
    }
</script>